import { useEffect } from 'react';

import { useNotificationCenterMarkAsReadMutation } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import logger from 'utils/logger';

import { useNotificationCenterSession } from './use-notification-center-session';
import { getReadContentCardsFromLocalStorage } from './utils';

/**
 * Syncs local (anonymous) read history to the server once, right after login - gated on the
 * `localReadsSyncStatus` Redux flag (not a mount-time transition check) so it stays correct
 * across this hook's own remounts, and on `isCurrent` (not just `isAuthenticated`) to avoid
 * firing under an ambiguous identity mid account-swap. Failure moves status to `failed`, not
 * `idle`, to avoid retrying in a tight loop; both reset via resetNotificationCenterSession on logout.
 */
export const useSyncLocalReadContentCardsOnLogin = ({ enabled }: { enabled: boolean }) => {
  const dispatch = useAppDispatch();
  const { isCurrent, sessionId } = useNotificationCenterSession();
  const localReadsSyncStatus = useAppSelector(selectors.loyalty.selectLocalReadsSyncStatus);
  const [markNotificationsAsRead] = useNotificationCenterMarkAsReadMutation();

  useEffect(() => {
    if (!enabled || !isCurrent || localReadsSyncStatus !== 'idle') {
      return;
    }

    const locallyReadNotificationIds = getReadContentCardsFromLocalStorage();
    if (locallyReadNotificationIds.length === 0) {
      dispatch(actions.loyalty.setLocalReadsSyncStatus({ status: 'synced', sessionId }));
      return;
    }

    dispatch(actions.loyalty.setLocalReadsSyncStatus({ status: 'syncing', sessionId }));

    markNotificationsAsRead({ variables: { notificationIds: locallyReadNotificationIds } })
      .then(({ data }) => {
        // Resolves successfully even when the server reports failure (a Boolean, not a throw).
        if (!data?.notificationCenterMarkAsRead) {
          logger.warn({ message: 'Failed to sync locally read content cards on login' });
          dispatch(actions.loyalty.setLocalReadsSyncStatus({ status: 'failed', sessionId }));
          return;
        }

        dispatch(
          actions.loyalty.mergeServerReadNotificationIds({
            ids: locallyReadNotificationIds,
            sessionId,
          })
        );
        dispatch(actions.loyalty.setLocalReadsSyncStatus({ status: 'synced', sessionId }));
      })
      .catch(error => {
        logger.warn({ error, message: 'Failed to sync locally read content cards on login' });
        dispatch(actions.loyalty.setLocalReadsSyncStatus({ status: 'failed', sessionId }));
      });
  }, [enabled, isCurrent, localReadsSyncStatus, sessionId, markNotificationsAsRead, dispatch]);
};
